import * as React from 'react';
import {
	filterDaysOptionsByMaxDate,
	filterMonthOptionsByMaxDate,
	filterYearOptionsByMaxDate,
	isDatePartsBeyondMax,
	MONTHS_OPTIONS,
	parseMaxDate,
	YEARS_OPTIONS,
} from './lib';
import { Body2AccentTypography, CaptionAccentTypography, CaptionTypography } from '@components/atoms';
import type { ListBoxOption } from '@components/molecules';
import { ListBox } from '@components/molecules';

type DateSelectInputLabels = {
	title: string;
	day: string;
	month: string;
	year: string;
};

type Props = {
	labels: DateSelectInputLabels;
	monthsOptions?: ListBoxOption[];
	daysOptions?: ListBoxOption[];
	yearsOptions?: ListBoxOption[];
	maxDate?: string;
	onDateChange: (date: string | null) => void;
	onDayChange?: (day?: number) => void;
	onMonthChange?: (month: number) => void;
	onYearChange?: (year: number) => void;
	required?: boolean;
};

const OptionComponent = ({ option, isSelected }: { option: ListBoxOption; isSelected?: boolean }) => {
	if (isSelected) {
		return <Body2AccentTypography className="text-[1.5rem] text-black">{option.label}</Body2AccentTypography>;
	}

	return <CaptionTypography className="text-[1.5rem] text-black">{option.label}</CaptionTypography>;
};

export const DateSelectInput: React.FC<Props> = ({
	labels,
	daysOptions,
	monthsOptions = MONTHS_OPTIONS,
	yearsOptions = YEARS_OPTIONS,
	maxDate,
	onDateChange,
	onDayChange,
	onMonthChange,
	onYearChange,
	required,
}) => {
	const [day, setDay] = React.useState<number>();
	const [month, setMonth] = React.useState<number>();
	const [year, setYear] = React.useState<number>();

	const parsedMaxDate = React.useMemo(() => parseMaxDate(maxDate), [maxDate]);

	const filteredYearsOptions = React.useMemo(
		() => filterYearOptionsByMaxDate(yearsOptions, parsedMaxDate),
		[yearsOptions, parsedMaxDate]
	);

	const filteredMonthsOptions = React.useMemo(
		() => filterMonthOptionsByMaxDate(monthsOptions, year, parsedMaxDate),
		[monthsOptions, year, parsedMaxDate]
	);

	const days: ListBoxOption[] = React.useMemo(() => {
		return filterDaysOptionsByMaxDate(month, year, parsedMaxDate, daysOptions);
	}, [month, year, parsedMaxDate, daysOptions]);

	const clearDaySelection = React.useCallback(() => {
		setDay(undefined);
		onDayChange?.();
		onDateChange(null);
	}, [onDayChange, onDateChange]);

	const onDaySelected = React.useCallback(
		(selectedDay: ListBoxOption['value']) => {
			setDay(selectedDay as number);
			onDayChange?.(Number(selectedDay));
		},
		[onDayChange]
	);

	const onMonthSelected = React.useCallback(
		(selectedMonth: ListBoxOption['value']) => {
			const nextMonth = Number(selectedMonth);
			setMonth(nextMonth);
			onMonthChange?.(nextMonth);

			if (day) {
				const yearToCheck: number = year ?? new Date().getFullYear();
				const monthDays: number = new Date(yearToCheck, nextMonth, 0).getDate();
				const exceedsMonthLength = day > monthDays;
				const exceedsMaxDay =
					year !== undefined && isDatePartsBeyondMax(year, nextMonth, day, parsedMaxDate);

				if (exceedsMonthLength || exceedsMaxDay) {
					clearDaySelection();
				}
			}
		},
		[day, year, onMonthChange, parsedMaxDate, clearDaySelection]
	);

	const onYearSelected = React.useCallback(
		(selectedYear: ListBoxOption['value']) => {
			const nextYear = Number(selectedYear);
			setYear(nextYear);
			onYearChange?.(nextYear);

			if (isDatePartsBeyondMax(nextYear, month, undefined, parsedMaxDate)) {
				setMonth(undefined);
				clearDaySelection();
				return;
			}

			if (day && isDatePartsBeyondMax(nextYear, month, day, parsedMaxDate)) {
				clearDaySelection();
			}
		},
		[onYearChange, parsedMaxDate, month, day, clearDaySelection]
	);

	React.useEffect(() => {
		if (day && month && year) {
			onDateChange(`${year}-${month >= 10 ? month : `0${month}`}-${day >= 10 ? day : `0${day}`}`);
		}
	}, [day, month, year, onDateChange]);

	const now: Date = new Date();

	return (
		<div className="flex w-full flex-col gap-[0.75rem]" data-aid="date-select-input">
			<CaptionAccentTypography className="text-black">{labels.title + (required ? '*' : '')}</CaptionAccentTypography>
			<div className="grid grid-cols-[1fr_1fr_1fr] gap-x-[0.75rem]">
				<div data-aid="month-select-box">
					<ListBox
						options={filteredMonthsOptions}
						onSelect={onMonthSelected}
						placeholder={labels.month}
						OptionComponent={OptionComponent}
						defaultOpenOnValue={now.getMonth()}
						value={month ?? null}
						textCenter
					/>
				</div>
				<div data-aid="day-select-box">
					<ListBox
						options={days}
						onSelect={onDaySelected}
						placeholder={labels.day}
						OptionComponent={OptionComponent}
						value={day ?? null}
						textCenter
					/>
				</div>
				<div data-aid="year-select-box">
					<ListBox
						options={filteredYearsOptions}
						onSelect={onYearSelected}
						placeholder={labels.year}
						OptionComponent={OptionComponent}
						defaultOpenOnValue={now.getFullYear()}
						value={year ?? null}
						textCenter
					/>
				</div>
			</div>
		</div>
	);
};
